<template>
  <div ref="container" class="container"></div>
  <div class="graphics-card-host">
    <GraphicsBackendControlCard
      :card="graphicsCard"
      locked-reason="three.js Line2/LineMaterial 是 WebGL 专用 ShaderMaterial 实现，本页固定 WebGL2"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import * as THREE from "three";
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'

import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import * as GeometryUtils from 'three/examples/jsm/utils/GeometryUtils.js';

const containerRef = useTemplateRef('container')
const graphicsCard = useExampleGraphicsCard({
  presetKey: 'none',
  lockedReason: 'three.js Line2/LineMaterial 是 WebGL 专用 ShaderMaterial 实现，本页固定 WebGL2',
  lockedBackend: 'webgl2',
})

const color1 = "#118AF0"
const color2 = "#00B825"
const color3 = "#52A6FF"

function createCanvasTexture(size = 256, color = "#118AF0") {
    const canvas = document.createElement('canvas')
    const halfSize = size / 2
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")!;
    ctx.beginPath()
    ctx.arc(halfSize, halfSize, halfSize, 0, 2 * Math.PI)
    ctx.fillStyle = color
    ctx.fill();
    ctx.beginPath()
    ctx.arc(halfSize, halfSize, halfSize / 2, 0, 2 * Math.PI)
    ctx.fillStyle = 'white'
    ctx.fill();
    const texture = new THREE.CanvasTexture(canvas)
    return texture
}

function createSphereMaterial3() {
    let sphereMaterial = new THREE.MeshBasicMaterial({
    });
    sphereMaterial.map = createCanvasTexture(512)
    return sphereMaterial;
}

function createSpritePoint() {
    const labelMaterial = new THREE.SpriteMaterial({
        map: createCanvasTexture(256),
        side: THREE.DoubleSide,
        transparent: true,
    });
    const label = new THREE.Sprite(labelMaterial)
    return label
}

function createSpriteCircle() {
    const labelMaterial = new THREE.SpriteMaterial({
        map: createCanvasTexture(256),
        side: THREE.DoubleSide,
        transparent: true,
    });
    const geometry = new THREE.CircleGeometry(5, 32);
    const material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    material.map = createCanvasTexture(256)
    const circle = new THREE.Mesh(geometry, material)
    return circle
}


const positions: any[] = [];
const colors: any[] = [];

const points = GeometryUtils.hilbert3D(new THREE.Vector3(0, 0, 0), 20.0, 1, 0, 1, 2, 3, 4, 5, 6, 7);

const spline = new THREE.CatmullRomCurve3(points);
const divisions = Math.round(12 * points.length);
const point = new THREE.Vector3();
const color = new THREE.Color("#118AF0");

for (let i = 0, l = divisions; i < l; i++) {
    const t = i / l;
    spline.getPoint(t, point);
    positions.push(point.x, point.y, point.z);
    colors.push(color.r, color.g, color.b);

}

function createDashLine() {

    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    geometry.setColors(colors);

    const matLine = new LineMaterial({
        // color: 0xffffff,
        // linewidth: 5, // in world units with size attenuation, pixels otherwise
        // vertexColors: true,
        // dashed: true,
        // dashSize: 0.5,
        // dashScale: 4,
        // dashOffset: 1,
        // alphaToCoverage: true,
        // linewidth: 2,
        // resolution: new THREE.Vector2(1000, 1000),

        color: new THREE.Color("#118AF0"),
        linewidth: 4,
        vertexColors: false,
        dashed: true,
        dashSize: 4,
        dashScale: 4,
        dashOffset: 4,
        gapSize: 4,
        resolution: new THREE.Vector2(1000, 1000),
    });

    const line = new Line2(geometry, matLine);
    line.computeLineDistances();

    // const geo = new THREE.BufferGeometry();
    // geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    // geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    // const matLineBasic = new THREE.LineBasicMaterial({ vertexColors: true });
    // const matLineDashed = new THREE.LineDashedMaterial({ vertexColors: true, scale: 2, dashSize: 1, gapSize: 1 });

    // const line1 = new THREE.Line(geo, matLineDashed);
    // line1.computeLineDistances();
    // line1.visible = true;

    return line
}

const createSegmentLine = (scene: THREE.Scene) => {
    const matLine = new LineMaterial({
        color: 0xffffff,
        linewidth: 1,
        worldUnits: true,
        vertexColors: true,
        alphaToCoverage: false,
        
        
    });

    const segmentsGeometry = new LineSegmentsGeometry();
    segmentsGeometry.setPositions(positions);
    segmentsGeometry.setColors(colors);

    const segments = new LineSegments2(segmentsGeometry, matLine);
    segments.computeLineDistances();
    segments.scale.set(1, 1, 1);

    scene.add(segments)
}

onMounted(() => {
    graphicsCard.markCreating()
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);

    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setAnimationLoop(animate);
    containerRef.value?.appendChild(renderer.domElement);
    graphicsCard.markReady('webgl2')

    const geometry = new THREE.SphereGeometry(1, 10, 10);

    const point = new THREE.Mesh(geometry, createSphereMaterial3());
    const line = createDashLine()
    const label = createSpritePoint()

    scene.add(line);

    createSegmentLine(scene)

    camera.position.z = 5;

    function animate() {
        // label.rotation.x += 0.01;
        // label.rotation.y += 0.01;
        renderer.render(scene, camera);
    }
})

</script>

<style scoped>
.container {
    width: 100vw;
    height: 100vh;
}

.graphics-card-host {
  position: fixed;
  top: 16px;
  left: 16px;
  width: min(340px, calc(100vw - 32px));
  z-index: 10;
}
</style>
